import { Group, Text, Tooltip } from '@mantine/core';
import { HiX } from 'react-icons/hi';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { HiMiniExclamationCircle, HiOutlineArrowSmallLeft } from 'react-icons/hi2';
import AppIcon from '@/components/AppIcon.tsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { get_dataset_parent_url } from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useUpdateDataset from '@/hooks/api/datasets/useUpdateDataset.ts';
import {
  Dataset,
  DatasetParentResponse,
  isDatasetRelationshipUnavailable,
} from '@/types/datasets.ts';
import { EditableText } from '@/components/EditableText.tsx';
import DatasetIcon from '@/components/DatasetIcon.tsx';

type props = {
  dataset: Dataset;
  setDatasetId: (id?: string) => void;
  isResultPage?: boolean;
};

const HEADER_HEIGHT = 60;

const DatasetViewerHeader = ({ dataset, setDatasetId, isResultPage = false }: props) => {
  const { mutate: updateDataset, isPending: isUpdatingDataset } = useUpdateDataset(dataset.id);

  const apiClient = useApiClient();
  const [parentError, setParentError] = useState<{ datasetId: string; message: string }>();
  const parentRequest = useRef(0);

  useEffect(() => {
    // Invalidate requests when switching datasets or leaving the viewer.
    return () => {
      parentRequest.current += 1;
    };
  }, [dataset.id]);

  const loadParentDataset = useCallback(async () => {
    const request = ++parentRequest.current;
    setParentError(undefined);
    try {
      const parentDataset = await apiClient.get<DatasetParentResponse>(
        get_dataset_parent_url(dataset.id),
      );
      if (request !== parentRequest.current) return;
      if (isDatasetRelationshipUnavailable(parentDataset.data)) {
        setParentError({ datasetId: dataset.id, message: 'This dataset has no parent.' });
        return;
      }
      setDatasetId(parentDataset.data.id);
    } catch {
      if (request !== parentRequest.current) return;
      setParentError({
        datasetId: dataset.id,
        message: 'The parent dataset could not be loaded. Try again.',
      });
    }
  }, [apiClient, dataset.id, setDatasetId]);

  return (
    <Group
      justify="space-between"
      align="center"
      px="sm"
      style={{ borderBottom: '1px solid #e0e0e0' }}
      h={HEADER_HEIGHT}
      mih={HEADER_HEIGHT}
      wrap={'nowrap'}
      bg={'white'}
      gap={'2px'}
    >
      <Group px={'4px'} gap={'2px'} wrap={'nowrap'} style={{ flex: 1, minWidth: 0 }}>
        {dataset.has_parent && (
          <StyledButton
            variant={'subtleIcon'}
            size={'sm'}
            aria-label="Open parent dataset"
            onClick={() => loadParentDataset()}
            ml={'-6px'}
          >
            <AppIcon Icon={HiOutlineArrowSmallLeft} />
          </StyledButton>
        )}
        {parentError?.datasetId === dataset.id && (
          <Text c="red" size="xs" role="alert">
            {parentError.message}
          </Text>
        )}

        <EditableText
          name={dataset.name}
          onSave={(newName) => updateDataset({ name: newName })}
          saving={isUpdatingDataset}
          size={'sm'}
          placeholder={'Name'}
          leftSection={
            dataset.processing_error ? (
              <Tooltip
                label={dataset.processing_error}
                styles={{ tooltip: { fontSize: '12px', padding: '6px 10px' } }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '20px',
                    width: '20px',
                  }}
                >
                  <AppIcon
                    Icon={HiMiniExclamationCircle}
                    size={'18px'}
                    iconProps={{ color: '#EE393D' }}
                  />
                </span>
              </Tooltip>
            ) : (
              <DatasetIcon dataset={dataset} size="20px" />
            )
          }
        />
      </Group>

      {!isResultPage && (
        <div style={{ flexShrink: 0 }}>
          <StyledButton
            size={'md'}
            variant={'subtleIcon'}
            aria-label="Close data viewer"
            onClick={() => {
              parentRequest.current += 1;
              setDatasetId(undefined);
            }}
            styles={{
              root: {
                padding: '5px',
                width: '25px',
                height: '25px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            }}
          >
            <HiX size={13} />
          </StyledButton>
        </div>
      )}
    </Group>
  );
};

export default DatasetViewerHeader;
